import { useRouterState } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import type { ReactNode } from 'react'

import { EmptyState } from '@aeris/ui'

import { useAuth } from '../lib/auth-context.ts'
import { moduleForPath } from '../lib/workspace.ts'

/**
 * A tela que o admin tirou desta conta não abre pela URL também.
 *
 * O menu deixou de oferecê-la, mas o menu não é o único caminho: um favorito
 * da barra do navegador, um endereço colado no WhatsApp, o botão de voltar e a
 * janela do Modo PC entram direto. Sem isto, "tirar Vendas do caixa" seria
 * verdade só até alguém digitar `/vendas`.
 *
 * Isto continua sendo **navegação**, não autorização, e a distinção importa
 * aqui mais do que em qualquer outro guarda do produto: quem fica com
 * Orçamentos mantém `sales.read`, e a API vai responder `GET /sales` para essa
 * pessoa mesmo com esta tela escondida. Esconder o caminho não tranca o dado —
 * trancar o dado é trocar o papel. A tela do administrador diz isso com todas
 * as letras, porque acreditar ter revogado um acesso que continua alcançável é
 * pior do que saber que não revogou.
 *
 * Uma rota que não é módulo — `/trocar-senha`, `/servidor` — não passa por
 * aqui, e uma que não estiver no mapa passa direto: um mapa incompleto não
 * pode virar uma tela que ninguém abre.
 */
export const PageGate = ({ children }: { children: ReactNode }): ReactNode => {
  const { showsPage } = useAuth()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  const module = moduleForPath(pathname)

  if (module !== null && !showsPage(module)) {
    return (
      <EmptyState
        icon={<Lock aria-hidden="true" className="h-8 w-8" />}
        title="Esta tela não está liberada para a sua conta"
        description="Quem administra o Aeris na sua empresa escolhe as telas de cada pessoa. Se você precisa desta, peça para liberarem."
      />
    )
  }

  return children
}
